import { Box, Stack } from '@mui/material';
import { ReactNode, useCallback } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';
import { AiSuggestionKind } from '@/types/schema';

interface AcceptRejectSuggestionButtonsProps {
  suggestionKind: AiSuggestionKind;
  onAcceptSuggestion: () => void;
  onRejectSuggestion: () => void;
  disableAccept?: boolean;
  disableReject?: boolean;
  /**
   * Tooltip content shown when hovering the accept button while it is
   * disabled. Only rendered when `disableAccept` is true.
   */
  acceptDisabledTooltip?: ReactNode;
}

const buttonSx = {
  width: '16px',
  height: '16px',
  minWidth: '16px',
  p: '2px',
};

// These inline accept/reject affordances are styled locally rather than through
// a shared Button `variant` so that changes to the global success/destructive
// button variants don't alter how they render at this tiny 16px size. They use
// a soft tinted fill with a colored icon on a neutral (transparent) base.
const acceptButtonSx = {
  ...buttonSx,
  border: 'none',
  backgroundColor: COLORS.FUNCTIONAL.SUCCESS[100],
  color: COLORS.FUNCTIONAL.SUCCESS[700],
  '&:hover': {
    backgroundColor: COLORS.FUNCTIONAL.SUCCESS[200],
  },
  '&:disabled': {
    backgroundColor: COLORS.GRAY[100],
    color: COLORS.GRAY[400],
  },
};

const rejectButtonSx = {
  ...buttonSx,
  border: 'none',
  backgroundColor: COLORS.FUNCTIONAL.ERROR[100],
  color: COLORS.FUNCTIONAL.ERROR[700],
  '&:hover': {
    backgroundColor: COLORS.FUNCTIONAL.ERROR[200],
  },
  '&:disabled': {
    backgroundColor: COLORS.GRAY[100],
    color: COLORS.GRAY[400],
  },
};

const tooltipButtonSlotSx = {
  display: 'flex',
  width: '16px',
  height: '16px',
  minWidth: '16px',
  '& > span': {
    display: 'flex',
    width: '16px',
    height: '16px',
  },
};

export function AcceptRejectSuggestionButtons({
  suggestionKind,
  onAcceptSuggestion,
  onRejectSuggestion,
  disableAccept = false,
  disableReject = false,
  acceptDisabledTooltip,
}: AcceptRejectSuggestionButtonsProps) {
  const trackUserEvent = useTrackUserEvent();

  const handleAcceptSuggestion = useCallback(() => {
    trackUserEvent('ai_suggestion accept', {
      suggestionKind,
      count: 1,
    });
    onAcceptSuggestion();
  }, [onAcceptSuggestion, suggestionKind, trackUserEvent]);

  const handleRejectSuggestion = useCallback(() => {
    trackUserEvent('ai_suggestion reject', {
      suggestionKind,
      count: 1,
    });
    onRejectSuggestion();
  }, [onRejectSuggestion, suggestionKind, trackUserEvent]);

  const acceptButton = (
    <IconButton
      icon={CheckIcon}
      iconProps={{
        size: 12,
        fill: 'transparent',
      }}
      ariaLabel="Accept suggestion"
      size="xs"
      variant="transparent"
      onClick={handleAcceptSuggestion}
      disabled={disableAccept}
      sx={acceptButtonSx}
      data-testid={`acceptSuggestion_${suggestionKind}`}
    />
  );

  return (
    <Stack direction="row" gap={0.5}>
      {disableAccept && acceptDisabledTooltip ? (
        <Box component="span" sx={tooltipButtonSlotSx}>
          <Tooltip title={acceptDisabledTooltip}>{acceptButton}</Tooltip>
        </Box>
      ) : (
        acceptButton
      )}
      <IconButton
        icon={XCloseIcon}
        iconProps={{
          size: 12,
          fill: 'transparent',
        }}
        ariaLabel="Reject suggestion"
        size="xs"
        variant="transparent"
        onClick={handleRejectSuggestion}
        disabled={disableReject}
        sx={rejectButtonSx}
        data-testid={`rejectSuggestion_${suggestionKind}`}
      />
    </Stack>
  );
}
